/**
 * Deterministic, offline Vancouver (NLM / ICMJE) reference-list linter.
 *
 * The same contract as the other reference linters (`src/lib/citation-check*.ts`):
 * pure string in, findings out, high precision, every finding a likely issue to review
 * rather than a verdict. It does not import the citation core. The words of each
 * finding live in the widget's strings, keyed by `rule`.
 *
 * Rules and where they come from:
 * - ICMJE Recommendations, "Preparing a Manuscript for Submission", References:
 *   "References should be numbered consecutively in the order in which they are first
 *   mentioned in the text", and journal titles are "abbreviated according to the style
 *   used for MEDLINE"; references follow NLM's sample references and Citing Medicine.
 * - NLM, "Samples of Formatted References for Authors of Journal Articles": "List the
 *   first six authors followed by et al." (NLM itself now lists all authors); the
 *   standard form "Halpern SD, Ubel PA, Caplan AL. Solid-organ transplantation in
 *   HIV-infected patients. N Engl J Med. 2002 Jul 25;347(4):284-7."; a DOI is written
 *   "doi: 10.1136/bmj.a2752." and the entry ends with a period.
 * - Citing Medicine, 2nd ed. (NLM), via the University of Bath's guide built on it
 *   (NCBI Bookshelf refuses automated reads): surname followed by initials without
 *   periods; no "and" before the last author; sentence-case article titles with no
 *   quotation marks; journal abbreviations without periods; "Year Month Day;volume
 *   (issue):pages" with no spaces; months as their first three letters; and "Do not
 *   repeat page numbers unless they are followed by a letter. For example 123-125
 *   becomes 123-5."
 */

import {
  type AuthorList,
  correctedAuthors,
  findLocator,
  journalSegment,
  type Locator,
  nameFormIssue,
  nlmMonth,
  parseAuthors,
  RE_IEEE_LOCATORS,
  readTitle,
  type TitlePart,
  undottedJournal,
  usesConjunction,
} from "@/lib/tool-kit/reference-checkers/medical-parts";
import {
  alphabeticalFinding,
  type CheckReport,
  duplicateFindings,
  type EntryReport,
  elidedRange,
  endsWithTerminal,
  type Finding,
  finishReport,
  type ListEntry,
  type ListRule,
  looksTitleCase,
  numberingFindings,
  parseNumberedList,
} from "@/lib/tool-kit/reference-checkers/numbered-list";

export type VancouverRule =
  | ListRule
  | "author-periods"
  | "author-comma"
  | "author-natural"
  | "author-and"
  | "et-al-threshold"
  | "et-al-early"
  | "title-quotes"
  | "title-case"
  | "journal-periods"
  | "ieee-locators"
  | "locator-spacing"
  | "page-range"
  | "month-format"
  | "doi-format"
  | "terminal-period";

export type VancouverSource = "nlm" | "citingMedicine" | "icmje";

/** Where each rule is written down. The link labels live in the widget's strings. */
export const VANCOUVER_SOURCES: Record<VancouverSource, string> = {
  nlm: "https://www.nlm.nih.gov/bsd/uniform_requirements.html",
  citingMedicine: "https://www.ncbi.nlm.nih.gov/books/NBK7256/",
  icmje:
    "https://www.icmje.org/recommendations/browse/manuscript-preparation/preparing-for-submission.html",
};

export type VancouverFinding = Finding<VancouverRule, VancouverSource>;
export type VancouverReport = CheckReport<VancouverRule, VancouverSource>;

/** NLM's sample list stops at six names before et al. */
const ET_AL_AFTER = 6;

function finding(
  rule: VancouverRule,
  confidence: "high" | "medium",
  source: VancouverSource,
  extra: Pick<VancouverFinding, "suggestion" | "values"> = {}
): VancouverFinding {
  return { rule, confidence, source, ...extra };
}

// --- authors -----------------------------------------------------------------------

const FORM_RULE = {
  natural: "author-natural",
  comma: "author-comma",
  periods: "author-periods",
} as const;

function authorFindings(list: AuthorList): VancouverFinding[] {
  const findings: VancouverFinding[] = [];
  const many = list.names.length > ET_AL_AFTER;
  const fixed = correctedAuthors(
    list,
    many ? ET_AL_AFTER : list.names.length,
    many || list.etAl
  );
  const form = nameFormIssue(list);
  if (form) {
    // One finding whose suggestion is the whole corrected element.
    findings.push(
      finding(FORM_RULE[form], "high", "nlm", { suggestion: fixed })
    );
  } else if (usesConjunction(list)) {
    findings.push(finding("author-and", "high", "nlm", { suggestion: fixed }));
  }
  if (many) {
    findings.push(
      finding("et-al-threshold", "high", "nlm", {
        suggestion: fixed,
        values: { count: list.names.length },
      })
    );
  } else if (list.etAl && list.names.length < ET_AL_AFTER) {
    findings.push(
      finding("et-al-early", "medium", "nlm", {
        values: { count: list.names.length },
      })
    );
  }
  return findings;
}

// --- title, journal, locator ---------------------------------------------------------

function titleFindings(
  text: string,
  title: TitlePart,
  locator: Locator | null
): VancouverFinding[] {
  const findings: VancouverFinding[] = [];
  if (title.quoted) {
    findings.push(
      finding("title-quotes", "high", "citingMedicine", {
        suggestion: `${title.title}.`,
      })
    );
  }
  if (looksTitleCase(title.title)) {
    findings.push(finding("title-case", "medium", "citingMedicine"));
  }
  const journal = locator ? journalSegment(text, title, locator) : null;
  const undotted = journal ? undottedJournalFix(journal) : null;
  if (undotted) {
    findings.push(undotted);
  }
  return findings;
}

function undottedJournalFix(journal: string): VancouverFinding | null {
  const fixed = undottedJournal(journal);
  return fixed
    ? finding("journal-periods", "high", "icmje", {
        suggestion: fixed,
        values: { found: journal, expected: fixed },
      })
    : null;
}

/** The locator block as NLM writes it: no spaces, a three-letter month, short pages. */
function nlmLocator(locator: Locator): string {
  const month = locator.month
    ? (nlmMonth(locator.month)?.expected ?? locator.month)
    : null;
  const date = [locator.year, month, locator.day].filter(Boolean).join(" ");
  let block = `${date};${locator.volume ?? ""}`;
  if (locator.issue !== null) {
    block += `(${locator.issue})`;
  }
  if (locator.first !== null) {
    block +=
      locator.last === null
        ? `:${locator.first}`
        : `:${elidedRange(locator.first, locator.last) ?? `${locator.first}-${locator.last}`}`;
  }
  return block;
}

function locatorFindings(locator: Locator): VancouverFinding[] {
  const findings: VancouverFinding[] = [];
  if (locator.spaced) {
    const expected = nlmLocator(locator);
    findings.push(
      finding("locator-spacing", "high", "citingMedicine", {
        suggestion: expected,
        values: { found: locator.raw.trim(), expected },
      })
    );
  }
  const short =
    locator.first && locator.last
      ? elidedRange(locator.first, locator.last)
      : null;
  if (short) {
    findings.push(
      finding("page-range", "medium", "citingMedicine", {
        suggestion: short,
        values: { found: `${locator.first}-${locator.last}`, expected: short },
      })
    );
  }
  const month = locator.month ? nlmMonth(locator.month) : null;
  if (month) {
    findings.push(
      finding("month-format", "medium", "citingMedicine", {
        suggestion: month.expected,
        values: { found: month.found, expected: month.expected },
      })
    );
  }
  return findings;
}

// --- DOI and the closing period ------------------------------------------------------

const RE_DOI_PREFIX = /\b(doi\s*:\s*)(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_OLD_DOI_URL =
  /\bhttps?:\/\/dx\.doi\.org\/(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))|\bhttp:\/\/doi\.org\/(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_ENDS_WITH_URL = /https?:\/\/\S+$/i;
const RE_ONLY_PERIOD = /^\.?$/;

interface DoiCheck {
  finding: VancouverFinding | null;
  /** The DOI closes the entry, so its fix carries the final period. */
  atEnd: boolean;
}

function doiCheck(text: string): DoiCheck {
  const trimmed = text.trim();
  const prefixed = RE_DOI_PREFIX.exec(trimmed);
  const wrongPrefix = prefixed && prefixed[1] !== "doi: " ? prefixed : null;
  const old = wrongPrefix || prefixed ? null : RE_OLD_DOI_URL.exec(trimmed);
  const match = wrongPrefix ?? old;
  if (!match) {
    return { finding: null, atEnd: false };
  }
  const doi = wrongPrefix ? wrongPrefix[2] : (match[1] ?? match[2]);
  const atEnd = RE_ONLY_PERIOD.test(
    trimmed.slice(match.index + match[0].length)
  );
  const expected = `doi: ${doi}${atEnd ? "." : ""}`;
  return {
    finding: finding("doi-format", "medium", "nlm", {
      suggestion: expected,
      values: { found: match[0], expected },
    }),
    atEnd,
  };
}

function periodFinding(text: string): VancouverFinding | null {
  const trimmed = text.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) || endsWithTerminal(trimmed)) {
    return null;
  }
  return finding("terminal-period", "high", "nlm");
}

// --- the check -----------------------------------------------------------------------

function checkEntry(entry: ListEntry): {
  findings: VancouverFinding[];
  surname: string | null;
} {
  const findings: VancouverFinding[] = [];
  const authors = parseAuthors(entry.text);
  const locator = findLocator(entry.text);
  if (authors) {
    findings.push(...authorFindings(authors));
    const title = readTitle(entry.text, authors.end);
    if (title) {
      findings.push(...titleFindings(entry.text, title, locator));
    }
  }
  if (locator) {
    findings.push(...locatorFindings(locator));
  } else if (RE_IEEE_LOCATORS.test(entry.text)) {
    findings.push(finding("ieee-locators", "high", "citingMedicine"));
  }
  const doi = doiCheck(entry.text);
  const ending = doi.atEnd ? null : periodFinding(entry.text);
  for (const item of [doi.finding, ending]) {
    if (item) {
      findings.push(item);
    }
  }
  return { findings, surname: authors?.names[0]?.surname ?? null };
}

/** Check a pasted Vancouver reference list. Pure — no network, no mutation. */
export function checkVancouverReferences(text: string): VancouverReport {
  const list = parseNumberedList(text);
  const surnames: (string | null)[] = [];
  const entries: EntryReport<VancouverRule, VancouverSource>[] = list.map(
    (entry) => {
      const result = checkEntry(entry);
      surnames.push(result.surname);
      return {
        position: entry.position,
        label: entry.label,
        text: entry.text,
        findings: result.findings,
      };
    }
  );
  const listFindings: VancouverFinding[] = [
    ...numberingFindings(list, "icmje"),
    ...duplicateFindings(list, "nlm"),
  ];
  const alphabetical = alphabeticalFinding(surnames, "icmje");
  if (alphabetical) {
    listFindings.push(alphabetical);
  }
  return finishReport(entries, listFindings);
}
